/*
 * Copyright (c) 2019 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import * as React from "react";
import { Trans } from "react-i18next";
import { FlexGrid, ErrorMessage, Loading, ErrorContainer } from "../_common";
import DocumentContainer from "./component/DocumentContainer/DocumentContainer";

import Header from "./component/Header/Header";
import DocumentPageLogic from "../../../Logic/Legal/DocumentPageLogic";
import DocumentFilter from "./component/DocumentFilter/DocumentFilter";
import { NoPolicyDataFound } from "../../../api/legal/models/legal";
import { extractServiceErrorCode } from "../../../api/serviceErrorDeducer";
import { Redirect } from "react-router";
import { isAxiosServerError } from "../../../api/errorDeducer";
import styles from "./DocumentPage.module.scss";
import { uiEvents } from "../../../app-message/events/uiEvents";
import classNames from "classnames";

interface Props {
  localizedPolicyId?: string;
}

interface State {
  activeCountry: string | null;
}

const UnknownError = (props: { error: Error }) => {
  const { error } = props;
  const errorCode = extractServiceErrorCode(error);
  switch (errorCode) {
    case 20001:
    case 40038:
      return <Redirect to={"/error/404"} />;
  }

  if (isAxiosServerError(error)) return <Redirect to={"/error/500"} />;

  return <ErrorContainer description={<ErrorMessage error={error} />} />;
};
const NoPolicyDataError = () => (
  <ErrorContainer
    title={<Trans i18nKey={"Document.error.noPolicy.title"}>No legal document yet</Trans>}
    description={
      <Trans i18nKey={"Document.error.noPolicy.description"}>
        We are working quickly on the document <br /> so please do check back soon!
      </Trans>
    }
    footer={
      <Trans i18nKey={"Document.error.noPolicy.footer"}>
        If you have any question or concern please contact <a href={"mailto:support@accelbyte.io"}>support@accelbyte.io</a>
      </Trans>
    }
  />
);

export const DocumentError = (props: { error: Error }) => {
  const { error } = props;
  switch (true) {
    case error instanceof NoPolicyDataFound:
      return <NoPolicyDataError />;
    default:
      return <UnknownError error={error} />;
  }
};

const onScroll = () => {
  const el = document.getElementById("scroll-to-top");
  if (!el) return;
  if (window.scrollY > 100) {
    el.style.visibility = "visible";
  } else {
    el.style.visibility = "hidden";
  }
};

export default class DocumentPage extends React.Component<Props, State> {
  logic: DocumentPageLogic;

  constructor(props: Props) {
    super(props);
    const { localizedPolicyId } = props;
    this.logic = new DocumentPageLogic({ localizedPolicyId });
    this.logic.subscribe(() => this.setState({}));
    this.state = {
      activeCountry: null,
    };
  }

  componentDidMount(): void {
    this.logic.init();
    window.addEventListener("scroll", onScroll);
  }

  componentWillUnmount(): void {
    window.removeEventListener("scroll", onScroll);
  }

  backToTop = () => {
    window.scrollTo(0, 0);
  };

  onCloseFilter = () => {
    uiEvents.onNavigationDrawerMenuClick(false);
  };

  renderPolicy() {
    const { policies, localizedPolicy, isFetchingPolicies, isFilterMenuShown, policiesError } = this.logic.state;
    const { activeCountry } = this.state;
    return (
      <>
        <Header />
        {isFetchingPolicies && !policies && <Loading />}
        {!isFetchingPolicies && !!policies && (
          <FlexGrid className={classNames(styles.documentPage, isFilterMenuShown ? styles.documentPageNoScroll : "")}>
            {
              <div
                className={isFilterMenuShown ? styles.documentFilterContainerShow : styles.documentFilterContainerHide}
              >
                <DocumentFilter
                  onCloseFilter={this.onCloseFilter}
                  policies={policies}
                  localizedPolicy={localizedPolicy}
                  className={styles.documentFilter}
                  isLoading={isFetchingPolicies}
                  onCountryChange={(countryCode) => {
                    this.setState({ activeCountry: countryCode });
                    this.logic.changeCountry(countryCode);
                  }}
                  onClickPolicy={this.logic.changeActivePolicy}
                  activeCountry={activeCountry}
                />
              </div>
            }
            {!!localizedPolicy && !policiesError && (
              <DocumentContainer
                key={localizedPolicy.id}
                policies={policies}
                localizedPolicy={localizedPolicy}
                className={styles.documentContainer}
                isLoading={isFetchingPolicies && !policies}
              />
            )}
            {!!policiesError && (
              <DocumentError error={policiesError} />
            )}
          </FlexGrid>
        )}
        <div id={"scroll-to-top"} className={styles.arrowTop} onClick={this.backToTop}>
          <div className={styles.text}>Back to the top</div>
        </div>
      </>
    );
  }

  render() {
    const { policiesError, isFetchingPolicies } = this.logic.state;
    return (
      <React.Fragment>
        {(!policiesError || policiesError instanceof NoPolicyDataFound) && this.renderPolicy()}
        {!!policiesError && !isFetchingPolicies && !(policiesError instanceof NoPolicyDataFound) && <DocumentError error={policiesError} />}
      </React.Fragment>
    );
  }
}
